import type { CSSProperties } from 'react';
import { z } from 'zod';

// Applicant-journey branding. Mirrors the backend canonical shape stored in
// tenants.customization_json — EXCEPT the logo, which is an object-storage key
// (never part of this blob; see the customization plan). Only small strings.
//
// Fonts we offer. Kept in sync with the backend validator's allow-list.
export const TYPOGRAPHY_OPTIONS = [
  'Inter',
  'Figtree',
  'Roboto',
  'Outfit',
  'Poppins',
  'Montserrat',
  'Playfair Display',
] as const;

export type TypographyStyle = (typeof TYPOGRAPHY_OPTIONS)[number];

// "#RRGGBB" (case-insensitive — <input type="color"> emits lowercase).
const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
const radius = z.number().int().min(0).max(24);

export const customizationSchema = z.object({
  fontColor: z.object({ heading: hex, bodyText: hex }),
  inputFieldColor: z.object({
    fill: hex,
    stroke: hex,
    label: hex,
    placeholder: hex,
    radius,
  }),
  buttonColor: z.object({ fill: hex, stroke: hex, text: hex, radius }),
  statusColor: z.object({ success: hex, error: hex }),
  typographyStyle: z.enum(TYPOGRAPHY_OPTIONS),
});

export type CustomizationSettings = z.infer<typeof customizationSchema>;

// Built-in defaults (identical to the designer's DEFAULT_CUSTOMIZATION, minus
// the logo). Used when a tenant has no override or the stored blob is unusable.
export const DEFAULT_CUSTOMIZATION: CustomizationSettings = {
  fontColor: { heading: '#303030', bodyText: '#666666' },
  inputFieldColor: {
    fill: '#FFFFFF',
    stroke: '#DFDFDF',
    label: '#000000',
    placeholder: '#A5A5A5',
    radius: 8,
  },
  buttonColor: { fill: '#0048FF', stroke: '#0048FF', text: '#FFFFFF', radius: 8 },
  statusColor: { success: '#00A92C', error: '#EF4444' },
  typographyStyle: 'Inter',
};

// Parse the stored blob into a full settings object. Tolerant by design:
// malformed / partial / absent JSON falls back to defaults (deep-merged) so a
// bad row can NEVER break the builder or a session — it just shows defaults.
export function parseCustomization(json: string | null | undefined): CustomizationSettings {
  if (!json) return structuredClone(DEFAULT_CUSTOMIZATION);
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    // Strict parse first; if the shape is fully valid, use it as-is.
    const strict = customizationSchema.safeParse(raw);
    if (strict.success) return strict.data;
    // Otherwise deep-merge known groups over defaults, keeping only valid values.
    return mergeOverDefaults(raw);
  } catch {
    return structuredClone(DEFAULT_CUSTOMIZATION);
  }
}

// Canonical JSON string to send to the backend. Always the full, valid shape.
export function serializeCustomization(settings: CustomizationSettings): string {
  return JSON.stringify(customizationSchema.parse(settings));
}

// Field-by-field merge: any individually-valid field from `raw` wins, else the
// default. Lets us salvage a partially-valid blob instead of dropping it whole.
function mergeOverDefaults(raw: Record<string, unknown>): CustomizationSettings {
  const merged = customizationSchema.deepPartial().safeParse(raw);
  const p = merged.success ? merged.data : {};
  const d = DEFAULT_CUSTOMIZATION;
  return {
    fontColor: { ...d.fontColor, ...p.fontColor },
    inputFieldColor: { ...d.inputFieldColor, ...p.inputFieldColor },
    buttonColor: { ...d.buttonColor, ...p.buttonColor },
    statusColor: { ...d.statusColor, ...p.statusColor },
    typographyStyle: p.typographyStyle ?? d.typographyStyle,
  };
}

// Maps settings → the `--brand-*` CSS custom properties the preview frames (and
// later the live session journey) consume. Set on a wrapper; children read
// `var(--brand-*)`. Logo is passed separately (a URL), never a CSS var.
export function toBrandCssVars(s: CustomizationSettings): CSSProperties {
  return {
    '--brand-heading': s.fontColor.heading,
    '--brand-body': s.fontColor.bodyText,
    '--brand-input-fill': s.inputFieldColor.fill,
    '--brand-input-stroke': s.inputFieldColor.stroke,
    '--brand-input-label': s.inputFieldColor.label,
    '--brand-input-placeholder': s.inputFieldColor.placeholder,
    '--brand-input-radius': `${s.inputFieldColor.radius}px`,
    '--brand-btn-fill': s.buttonColor.fill,
    '--brand-btn-stroke': s.buttonColor.stroke,
    '--brand-btn-text': s.buttonColor.text,
    '--brand-btn-radius': `${s.buttonColor.radius}px`,
    '--brand-status-success': s.statusColor.success,
    '--brand-status-error': s.statusColor.error,
    '--brand-font': `"${s.typographyStyle}", sans-serif`,
  } as CSSProperties;
}
